import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, formatApiError } from "./api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const tok = localStorage.getItem("access_token");
    if (!tok) {
      setUser(false);
      setReady(true);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      // Token invalid/expired: log, drop it and treat the user as anonymous.
      console.warn("AuthProvider: /auth/me failed, clearing token", err?.response?.status || err?.message);
      localStorage.removeItem("access_token");
      setUser(false);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const register = useCallback(async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    // New signup requires email verification → no auto-login.
    // Only auto-set user when we receive an access_token (multi-role add flow).
    if (data.access_token) {
      localStorage.setItem("access_token", data.access_token);
      setUser(data.user);
    }
    return data; // caller inspects `requires_verification`, `role_added`, `user`, `access_token`
  }, []);

  const setActiveRole = useCallback(async (role) => {
    const { data } = await api.post("/auth/set-active-role", { role });
    if (data.access_token) localStorage.setItem("access_token", data.access_token);
    setUser(data.user);
    return data.user;
  }, []);

  const addRole = useCallback(async (payload) => {
    const { data } = await api.post("/auth/add-role", payload);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); }
    catch (err) { console.warn("AuthProvider: logout request failed", err?.message); }
    localStorage.removeItem("access_token");
    setUser(false);
  }, []);

  return (
    <AuthCtx.Provider value={{ user, ready, login, register, logout, refresh, setActiveRole, addRole, formatApiError }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
